import { useState, useEffect } from 'react';
import { getLeadBrief } from '../../../api/leadApi';
import './LeadBriefPanel.css';

const SENTIMENT_META = {
  WARMING: { label: 'Warming ↑', cls: 'lbp__sentiment--warming' },
  NEUTRAL: { label: 'Neutral →', cls: 'lbp__sentiment--neutral' },
  COOLING: { label: 'Cooling ↓', cls: 'lbp__sentiment--cooling' },
};

function Chips({ items, cls }) {
  if (!items || items.length === 0) return <span className="lbp__none">None</span>;
  return (
    <div className="lbp__chip-list">
      {items.map((item, i) => <span key={i} className={`lbp__chip ${cls}`}>{item}</span>)}
    </div>
  );
}

function formatCurrency(val) {
  if (!val) return null;
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
  return `₹${val.toLocaleString('en-IN')}`;
}

export default function LeadBriefPanel({ leadId }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    getLeadBrief(leadId)
      .then(setBrief)
      .catch(ex => setError(ex.message))
      .finally(() => setLoading(false));
  }, [leadId]);

  if (loading) return <div className="lbp__loading">Loading brief...</div>;
  if (error) return <div className="lbp__error">{error}</div>;
  if (!brief) return null;

  const sentMeta = SENTIMENT_META[brief.overallSentiment] || SENTIMENT_META.NEUTRAL;

  return (
    <div className="lbp__panel">
      {/* Header snapshot */}
      <div className="lbp__snapshot">
        <div className="lbp__snapshot-left">
          <div className="lbp__lead-name">{brief.leadName}</div>
          <div className="lbp__lead-phone">{brief.phone}</div>
          {brief.email && <div className="lbp__lead-email">{brief.email}</div>}
        </div>
        <div className="lbp__snapshot-right">
          {brief.estimatedAum && (
            <div className="lbp__aum">{formatCurrency(brief.estimatedAum)}</div>
          )}
          {brief.interestLevel && (
            <span className={`lbp__interest lbp__interest--${brief.interestLevel.toLowerCase()}`}>
              {brief.interestLevel}
            </span>
          )}
          <span className={`lbp__sentiment ${sentMeta.cls}`}>{sentMeta.label}</span>
        </div>
      </div>

      {/* Conversation summary */}
      {brief.conversationSummary && (
        <div className="lbp__section">
          <div className="lbp__section-title">Conversation Summary</div>
          <p className="lbp__summary-text">{brief.conversationSummary}</p>
        </div>
      )}

      {/* Key interests */}
      <div className="lbp__section">
        <div className="lbp__section-title">Key Interests</div>
        <Chips items={brief.keyInterests} cls="lbp__chip--interest" />
      </div>

      {/* Open objections */}
      <div className="lbp__section">
        <div className="lbp__section-title">Open Objections <span className="lbp__section-hint">(address in meeting)</span></div>
        <Chips items={brief.openObjections} cls="lbp__chip--objection" />
      </div>

      {/* Expectations set */}
      {brief.expectationsSet?.length > 0 && (
        <div className="lbp__section">
          <div className="lbp__section-title">Expectations Already Set <span className="lbp__section-hint">(align with these)</span></div>
          <Chips items={brief.expectationsSet} cls="lbp__chip--expectation" />
        </div>
      )}

      {/* Flags */}
      {brief.flags?.length > 0 && (
        <div className="lbp__section">
          <div className="lbp__section-title lbp__section-title--warn">⚠️ Flags</div>
          {brief.flags.map((flag, i) => (
            <div key={i} className="lbp__flag-card">⚠️ {flag}</div>
          ))}
        </div>
      )}

      {/* Talking points */}
      {brief.suggestedTalkingPoints?.length > 0 && (
        <div className="lbp__section">
          <div className="lbp__section-title">Suggested Talking Points</div>
          <ul className="lbp__talking-points">
            {brief.suggestedTalkingPoints.map((pt, i) => (
              <li key={i}>{pt}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="lbp__footer-meta">
        {brief.touchpointCount} touchpoints logged
      </div>
    </div>
  );
}
